import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useScrollToTop from "../../../hooks/useScrollToTop";
import { useToast } from "../../../Components/Common/Toast/ToastContext";
import { Formik, Form, Field, type FormikHelpers } from "formik";
import * as Yup from "yup";
import { CHAR_LIMIT, CHAR_MIN } from "../../../utils/Common/Constant";
import {
  limitMsg,
  minMsg,
} from "../../../Components/Common/ErrorMessage/ErrorMessage";
import { useGcLayout } from "../GcLayout";
import Table from "../../../Components/Table/Table";
import Card from "../GcProjectDetails/components/Card";
import Pagination from "../../../Components/Common/Pagination/Pagination";
import Input from "../../../Components/Common/Input/Input";
import Button from "../../../Components/Common/Button/Button";
import useGetSubContractors from "../../../hooks/useGetSubContractors";
import SubContractorTableSkeleton from "./ManageSubContractorTableSkeleton";
import CustomModal from "../../../Components/Common/CustomModal/CustomModal";
import PhoneInput from "../../../Components/Common/PhoneInput/PhoneInput";
import {
  createSubContractor,
  updateSubContractor,
  deleteSubContractor,
} from "../../../services/subContractorService";
import "./ManageSubContractor.scss";
import NetworkError from "Components/Common/NetworkError/NetworkError";

interface Subcontractor extends Record<string, unknown> {
  id: string;
  companyName: string;
  email: string;
  phone: string;
}

interface SubcontractorForm {
  name: string;
  email: string;
  phoneNumber: string;
}

const validationSchema = Yup.object({
  name: Yup.string()
    .required("Name is required")
    .max(CHAR_LIMIT.NAME, limitMsg(CHAR_LIMIT.NAME)),
  email: Yup.string()
    .required("Email is required")
    .max(CHAR_LIMIT.EMAIL, limitMsg(CHAR_LIMIT.EMAIL))
    .email("Must be a valid email"),
  phoneNumber: Yup.string()
    .test("phone-required", "Phone Number is required", (val) => {
      if (!val) return false;
      return val.replace(/\D/g, "").length > 3;
    })
    .test("phone-min", minMsg(CHAR_MIN.CONTACT), (val) => {
      if (!val) return true;
      const digits = val.replace(/\D/g, "");
      if (digits.length <= 3) return true;
      return val.length >= CHAR_MIN.CONTACT;
    })
    .max(CHAR_LIMIT.PHONE, limitMsg(CHAR_LIMIT.PHONE)),
});

export default function SubContractor() {
  const navigate = useNavigate();
  const location = useLocation();
  // Set by the SOV screen's "Add new subcontractor": go back there after saving.
  const returnTo = (location.state as { returnTo?: string } | null)?.returnTo;
  const { setCrumbs } = useGcLayout();

  useEffect(() => {
    setCrumbs([
      { label: "My Projects", link: "/gc-dashboard" },
      { label: "Manage Subcontractors" },
    ]);
  }, []);

  const [page, setPage] = useState(1);
  const {
    subContractors: apiData,
    total,
    totalPages,
    loading,
    error: fetchError,
    refetch,
  } = useGetSubContractors({ page, limit: 10 });

  const tableRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLDivElement>(null);
  const [subcontractors, setSubcontractors] = useState<Subcontractor[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState("");
  const showToast = useToast();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedSubcontractor, setSelectedSubcontractor] =
    useState<Subcontractor | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  useEffect(() => {
    setSubcontractors(apiData as Subcontractor[]);
  }, [apiData]);

  useScrollToTop(page);

  const getFormInitialValues = (): SubcontractorForm => {
    if (editingId) {
      const row = subcontractors.find((s) => s.id === editingId);
      if (row) {
        return {
          name: row.companyName,
          email: row.email,
          phoneNumber: row.phone,
        };
      }
    }
    return { name: "", email: "", phoneNumber: "" };
  };

  const openEditForm = (row: Subcontractor) => {
    setSubmitError("");
    setEditingId(row.id);
    setTimeout(
      () =>
        formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
      100,
    );
  };

  const closeForm = () => {
    setEditingId(null);
    setSubmitError("");
  };

  const handleSubmit = async (
    values: SubcontractorForm,
    { resetForm }: FormikHelpers<SubcontractorForm>,
  ) => {
    const payload = {
      companyName: values.name.trim(),
      email: values.email.trim(),
      phone: values.phoneNumber.trim(),
    };

    setSubmitting(true);
    setSubmitError("");
    if (editingId) {
      const { data, error: apiError } = await updateSubContractor(
        editingId,
        payload,
      );
      setSubmitting(false);

      if (apiError) {
        const errorMessage =
          (apiError as any)?.raw?.fields?.["body.companyName"] ||
          (apiError as any)?.raw?.fields?.["body.phone"] ||
          (apiError as any)?.raw?.fields?.["body.email"] ||
          (apiError as any)?.message ||
          "Failed to update subcontractor.";

        setSubmitError(errorMessage);
        showToast(errorMessage);
        return;
      }

      setSubcontractors((prev) =>
        prev.map((item) =>
          item.id === editingId ? { ...item, ...payload } : item,
        ),
      );
      closeForm();
      showToast("Subcontractor updated successfully.");
      return;
    }

    const { data: createdData, error: apiError } =
      await createSubContractor(payload);
    setSubmitting(false);

    if (apiError) {
      const errorMessage =
        (apiError as any)?.raw?.fields?.["body.companyName"] ||
        (apiError as any)?.raw?.fields?.["body.email"] ||
        (apiError as any)?.raw?.fields?.["body.phone"] ||
        (apiError as any)?.message ||
        "Failed to update subcontractor.";

      setSubmitError(errorMessage);
      showToast(errorMessage);
      return;
    }

    const created = (
      createdData as { data?: { id?: string; companyName?: string } } | null
    )?.data;
    if (returnTo && created?.id) {
      try {
        sessionStorage.setItem(
          "sov_new_subcontractor",
          JSON.stringify({ id: created.id, name: created.companyName ?? "" }),
        );
      } catch {}
      navigate(returnTo, { replace: true });
      return;
    }

    resetForm();
    closeForm();
    refetch();
    setTimeout(
      () =>
        tableRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        }),
      100,
    );
  };

  const openDeleteModal = (row: Subcontractor) => {
    setSelectedSubcontractor(row);
    setDeleteError("");
    setShowDeleteModal(true);
  };

  const handleCancelDelete = () => {
    if (deleting) return;
    setShowDeleteModal(false);
    setSelectedSubcontractor(null);
    setDeleteError("");
  };

  const handleConfirmDelete = async () => {
    if (!selectedSubcontractor) return;
    setDeleting(true);
    setDeleteError("");
    const { error: apiError } = await deleteSubContractor(
      selectedSubcontractor.id,
    );
    setDeleting(false);
    if (apiError) {
      setDeleteError(
        (apiError as any)?.message || "Failed to delete subcontractor.",
      );
      return;
    }
    setSubcontractors((prev) =>
      prev.filter((item) => item.id !== selectedSubcontractor.id),
    );
    setShowDeleteModal(false);
    setSelectedSubcontractor(null);
  };

  const columns = [
    { header: "Name", accessor: "companyName" as const },
    {
      header: "Email",
      render: (row: Subcontractor) =>
        row.email || <span className="muted">-</span>,
    },
    {
      header: "Phone",
      render: (row: Subcontractor) =>
        row.phone || <span className="muted">-</span>,
    },
    {
      header: "Actions",
      render: (row: Subcontractor) => (
        <div className="rowActions">
          <Button
            type="button"
            className="btn secondary sm"
            onClick={() => openEditForm(row)}
          >
            Edit
          </Button>
          <Button
            type="button"
            className="btn danger sm"
            onClick={() => openDeleteModal(row)}
          >
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="subContractorSec GCVALA" ref={formRef}>
      <main className="subContractorPage">
        <Button
          className="mobile-back-btn"
          type="button"
          onClick={() => navigate(-1)}
        >
          ← Back
        </Button>
        <div className="sectionTitle">Manage Subcontractors</div>
        <div className="sectionSub">Add, edit, and manage subcontractors.</div>

        <div className="subcontractorModalOverlay container-fluid">
          <div
            className="subcontractorModal"
            role="dialog"
            aria-modal="true"
            aria-label={editingId ? "Edit Subcontractor" : "Add Subcontractor"}
            onClick={(event: React.MouseEvent<HTMLDivElement>) =>
              event.stopPropagation()
            }
          >
            <div className="subcontractorModalHeader">
              <h3>{editingId ? "Edit Subcontractor" : "Add Subcontractor"}</h3>
            </div>

            <Formik
              initialValues={getFormInitialValues()}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
              enableReinitialize
            >
              <Form>
                <div className="row">
                  <div className="formField col-md-6">
                    <label htmlFor="name">Name *</label>
                    <Field name="name">
                      {({
                        field,
                        form,
                        meta,
                      }: {
                        field: React.InputHTMLAttributes<HTMLInputElement>;
                        form: {
                          setFieldTouched: (
                            name: string,
                            touched: boolean,
                            validate: boolean,
                          ) => void;
                        };
                        meta: { touched: boolean; error?: string };
                      }) => (
                        <>
                          <Input
                            {...field}
                            className=""
                            id="name"
                            type="text"
                            placeholder="Enter your name"
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>,
                            ) => {
                              (
                                field.onChange as React.ChangeEventHandler<HTMLInputElement>
                              )(e);
                              form.setFieldTouched("name", true, false);
                            }}
                          />
                          {meta.touched && meta.error && (
                            <p className="fieldError">{meta.error}</p>
                          )}
                        </>
                      )}
                    </Field>
                  </div>

                  <div className="formField col-md-6">
                    <label htmlFor="email">Email Address *</label>
                    <Field name="email">
                      {({
                        field,
                        form,
                        meta,
                      }: {
                        field: React.InputHTMLAttributes<HTMLInputElement>;
                        form: {
                          setFieldTouched: (
                            name: string,
                            touched: boolean,
                            validate: boolean,
                          ) => void;
                        };
                        meta: { touched: boolean; error?: string };
                      }) => (
                        <>
                          <Input
                            {...field}
                            className=""
                            id="email"
                            type="email"
                            placeholder="email@gmail.com"
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>,
                            ) => {
                              (
                                field.onChange as React.ChangeEventHandler<HTMLInputElement>
                              )(e);
                              form.setFieldTouched("email", true, false);
                            }}
                          />
                          {meta.touched && meta.error && (
                            <p className="fieldError">{meta.error}</p>
                          )}
                        </>
                      )}
                    </Field>
                  </div>

                  <div className="formField col-md-6">
                    <label htmlFor="phoneNumber">Phone Number *</label>
                    <Field name="phoneNumber">
                      {({
                        form,
                        meta,
                      }: {
                        form: {
                          setFieldValue: (name: string, value: string) => void;
                          setFieldTouched: (
                            name: string,
                            touched: boolean,
                            validate: boolean,
                          ) => void;
                        };
                        meta: {
                          touched: boolean;
                          error?: string;
                          value: string;
                        };
                      }) => (
                        <>
                          <PhoneInput
                            value={meta.value}
                            onChange={(value) => {
                              form.setFieldValue("phoneNumber", value);
                              form.setFieldTouched("phoneNumber", true, false);
                            }}
                            error={!!(meta.touched && meta.error)}
                          />
                          {meta.touched && meta.error && (
                            <p className="fieldError">{meta.error}</p>
                          )}
                        </>
                      )}
                    </Field>
                  </div>
                </div>

                {submitError && (
                  <p className="fieldError" style={{ marginBottom: 8 }}>
                    {submitError}
                  </p>
                )}
                <div className="formActions">
                  <Button
                    type="button"
                    className="btn secondary sm"
                    onClick={closeForm}
                    disabled={submitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="btn teal sm"
                    disabled={submitting}
                  >
                    {submitting
                      ? "Submitting…"
                      : editingId
                        ? "Update Subcontractor"
                        : "Submit"}
                  </Button>
                </div>
              </Form>
            </Formik>
          </div>
        </div>

        <div ref={tableRef}>
          <Card title="Subcontractors">
            {loading ? (
              <SubContractorTableSkeleton />
            ) : fetchError ? (
              <NetworkError />
            ) : subcontractors.length ? (
              <>
                <Table<Subcontractor> data={subcontractors} columns={columns} />
                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  total={total}
                  limit={10}
                  onPageChange={setPage}
                />
              </>
            ) : (
              <div className="emptyState">
                <p>No subcontractors added yet.</p>
              </div>
            )}
          </Card>
        </div>
      </main>

      <CustomModal
        className="subContractorModal"
        show={showDeleteModal}
        handleClose={handleCancelDelete}
        title="Delete Subcontractor"
        isHeaderVisible={true}
        isCrossBtn={false}
        isFooterVisible={true}
        backdrop={true}
        keyboard={true}
        footerContent={
          <>
            <Button
              type="button"
              className="btn secondary sm"
              onClick={handleCancelDelete}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="btn danger sm"
              onClick={handleConfirmDelete}
              disabled={deleting}
            >
              {deleting ? "Deleting..." : "Delete"}
            </Button>
          </>
        }
      >
        <p>
          Are you sure you want to delete{" "}
          <strong>{selectedSubcontractor?.companyName}</strong>? This action
          cannot be undone.
        </p>
        {deleteError && (
          <p
            style={{
              color: "var(--red, #a32d2d)",
              fontSize: 13,
              marginTop: 10,
            }}
          >
            {deleteError}
          </p>
        )}
      </CustomModal>
    </div>
  );
}
